import { NewCard } from "@components/Cards/Templates/newCard";
import { TENANT } from "@types/tenant";
import { sendRegisterDeviceRequest } from "@services/API/Requests/DeviceResgistrationRequests";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import PasswordResetModal from "@components/Modals/PasswordResetModal";
import WarningModal from "@components/Modals/WarningModal";
import { CertificateInformationCard } from "./CertificateInformationCard";

interface SecuritySettingsCardProps {
  tenantData: TENANT;
  updateTenantData: (data: TENANT) => void;
  fetchTenantData: () => void;
  loading: boolean;
  setError: (error: any) => void;
  fromViewPage?: boolean;
}

export const SecuritySettingsCard: React.FC<SecuritySettingsCardProps> = ({
  tenantData,
  updateTenantData,
  fetchTenantData,
  loading,
  setError,
  fromViewPage = false,
}: SecuritySettingsCardProps) => {
  const errorToast = (msg: string) => toast.error(msg);

  const [password, setPassword] = useState("");
  const [passwordResetModalOpen, setPasswordResetModalOpen] = useState(false);
  const [certDownloading, setCertDownloading] = useState(false);

  const [certificateExpired, setCertificateExpired] = useState(false);

  const [warningModalOpen, setWarningModalOpen] = useState(false);

  const [warningModalTitle, setWarningModalTitle] = useState("");
  const [warningModalBody, setWarningModalBody] = useState("");
  const [warningModalConfirmText, setWarningModalConfirmText] = useState("");

  const downloadClientCertificate = async (password: string) => {
    if (warningModalOpen) {
      setWarningModalOpen(false);
    }

    setCertDownloading(true);
    const result = await sendRegisterDeviceRequest(
      tenantData.id,
      tenantData.code,
      password || "",
      tenantData.active_certificates || false,
    );
    if (result?.error) {
      setError(result.error);
      errorToast(result.error);
    } else {
      await fetchTenantData(false);
      updateTenantData("active_certificates", true);
    }
    setCertDownloading(false);
    setPassword("");
  };

  const handlePasswordResetRequest = (password: string) => {
    setPassword(password);
    if (!tenantData.active_certificates || certificateExpired) {
      downloadClientCertificate(password);
    } else {
      setPasswordResetModalOpen(false);
      setWarningModalTitle("証明書パスワードリセット");
      setWarningModalBody(
        "証明書を発行すると旧証明書が利用不可となりログインできなくなりますが、よろしいですか？",
      );
      setWarningModalConfirmText("はい");
      setWarningModalConfirmAction(() => () => {
        downloadClientCertificate(password);
        setWarningModalOpen(false);
      });
      setWarningModalOpen(true);
    }
  };

  const handleCertificateCheckboxChange = () => {
    if (!tenantData.certificate_auth_enabled) {
      setWarningModalTitle("クライアント証明有効化");
      setWarningModalBody(
        "クライアント証明をONにすると端末に証明書をインストールしないとログインできなくなりますが、よろしいですか？",
      );
      setWarningModalConfirmText("はい");
      setWarningModalConfirmAction(() => () => {
        updateTenantData("certificate_auth_enabled", true);
        setWarningModalOpen(false);
      });
    } else {
      if (certificateExpired) {
        updateTenantData("certificate_auth_enabled", false);
        return;
      } else {
        setWarningModalTitle("クライアント証明無効化");
        setWarningModalBody(
          "クライアント証明を無効化しますか？\n無効化後、以前の証明書は利用できなくなります。",
        );
        setWarningModalConfirmText("はい");
        setWarningModalConfirmAction(() => () => {
          updateTenantData("certificate_auth_enabled", false);
          setWarningModalOpen(false);
        });
      }
    }
    setWarningModalOpen(true);
  };

  const [warningModalConfirmAction, setWarningModalConfirmAction] = useState(
    () => downloadClientCertificate,
  );

  useEffect(() => {
    if (
      tenantData.active_certificates &&
      tenantData?.active_certificate?.expires_at
    ) {
      const expirationDate = new Date(
        tenantData?.active_certificate?.expires_at,
      );
      const now = new Date();
      setCertificateExpired(expirationDate < now);
    } else {
      setCertificateExpired(false);
    }
  }, [
    tenantData.active_certificates,
    tenantData?.active_certificate?.expires_at,
  ]);

  return (
    <>
      <NewCard
        header="セキュリティ設定"
        bodyLabel=""
        loading={loading}
        bodyContents={
          <div>
            <h2 className="mb-6 label-light-blue-xl">ログイン方法設定</h2>
            <label className="flex items-center text-xl ml-5 mb-2">
              <input
                type="checkbox"
                className="mr-5 w-5 h-5 accent-blue-600"
                checked={tenantData?.local_auth}
                onChange={() =>
                  updateTenantData("local_auth", !tenantData?.local_auth)
                }
              />
              ローカル認証
            </label>

            <div className="mt-5 divider" />

            <h2 className="mt-6 mb-6 label-light-blue-xl">電話帳参照制限</h2>
            <label className="flex items-center text-xl ml-5 mb-2">
              <input
                type="checkbox"
                className="mr-5 w-5 h-5 accent-blue-600"
                checked={tenantData?.require_passcode_for_phonebook}
                onChange={() =>
                  updateTenantData(
                    "require_passcode_for_phonebook",
                    !tenantData?.require_passcode_for_phonebook,
                  )
                }
              />
              電話帳参照時にパスコードの入力を求める
            </label>

            <label className="flex items-center text-xl ml-5 mb-2">
              <input
                type="checkbox"
                className="mr-5 w-5 h-5 accent-blue-600"
                checked={tenantData?.auth_by_phone_number}
                onChange={() =>
                  updateTenantData(
                    "auth_by_phone_number",
                    !tenantData?.auth_by_phone_number,
                  )
                }
              />
              電話番号認証
            </label>

            {fromViewPage && (
              <>
                <div className="mt-5 divider" />

                <h2 className="mt-6 mb-6 label-light-blue-xl">
                  クライアント証明設定
                </h2>
                <div>
                  <label className="flex items-center text-xl ml-5 mb-2">
                    <input
                      type="checkbox"
                      className="mr-5 w-5 h-5 accent-blue-600"
                      checked={tenantData?.certificate_auth_enabled}
                      onChange={() => {
                        handleCertificateCheckboxChange(
                          !tenantData?.certificate_auth_enabled,
                        );
                      }}
                    />
                    クライアント証明
                  </label>
                  <div>
                    {!tenantData?.active_certificates &&
                      tenantData?.certificate_auth_enabled && (
                        <div
                          className={`flex justify-center ml-14 mt-2 mb-2 w-55 bg-blue-500 text-white px-4 py-4 rounded-lg items-center hover:bg-blue-400 hover:cursor-pointer disabled:bg-slate-400 disabled:cursor-not-allowed ${
                            certDownloading
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          onClick={() => setPasswordResetModalOpen(true)}
                        >
                          クライアント証明発行
                        </div>
                      )}
                    {tenantData?.active_certificates &&
                      tenantData?.certificate_auth_enabled && (
                        <>
                          {tenantData?.active_certificate && (
                            <CertificateInformationCard
                              tenantData={tenantData}
                            />
                          )}
                          <div
                            className={`flex justify-center ml-5 mt-2 mb-2 w-60 bg-blue-500 text-white px-4 py-4 rounded-lg items-center hover:bg-blue-400 hover:cursor-pointer disabled:bg-slate-400 disabled:cursor-not-allowed ${
                              certDownloading
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }`}
                            onClick={() => setPasswordResetModalOpen(true)}
                          >
                            証明書再発行
                          </div>
                        </>
                      )}
                  </div>
                </div>
              </>
            )}
          </div>
        }
      />
      <PasswordResetModal
        isOpen={passwordResetModalOpen}
        onRequestClose={function (): void {
          setPasswordResetModalOpen(false);
        }}
        onPasswordResetSuccess={handlePasswordResetRequest}
        isReset={tenantData?.active_certificates}
        confirmButtonText={"発行する"}
      />
      <WarningModal
        title={warningModalTitle}
        body={warningModalBody.split("\n").map((line, index) => (
          <p key={index} className="mb-2">
            {line}
          </p>
        ))}
        confirmText={warningModalConfirmText}
        isOpen={warningModalOpen}
        onRequestClose={() => setWarningModalOpen(false)}
        onConfirm={() => warningModalConfirmAction()}
        confirmButtonColor="bg-blue-500"
      />
    </>
  );
};
